import { useLocation, useNavigate } from 'react-router';
import { CALENDARS, OUTLOOK_CALENDAR } from '@quadro/domain';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import type { IconName } from '../components/icons';
import { useNotes, useNotices, useRoutines, useTasks } from '../data/queries';
import { signOut } from '../lib/rpc';
import { openSheet } from '../state/sheets';
import { useUI } from '../state/ui';
import { useWorkspace } from '../state/workspace';
import { useOutlookConnected } from '../features/agenda/agendaEvents';
import { openPalette } from '../features/search/CommandPalette';

function Item({ icon, dot, label, count, current, onClick }: { icon?: IconName; dot?: string; label: string; count?: number; current?: boolean; onClick: () => void }) {
  return (
    <button className="sb-item" onClick={onClick} aria-current={current ? 'page' : undefined}>
      <span className="sb-ico">{dot ? <span className="sb-dot" style={{ background: dot }} /> : <span><Icon name={icon!} /></span>}</span>
      <span className="lbl">{label}</span>
      {count ? <span className="n">{count}</span> : null}
    </button>
  );
}

export function Sidebar() {
  const ws = useWorkspace();
  const nav = useNavigate();
  const path = useLocation().pathname;
  const { scope, setScope, set, notesFilter, hiddenCalendars } = useUI();
  const outlookOn = useOutlookConnected();
  const notices = (useNotices().data ?? []).filter(n => !n.readAt).length;
  const tasks = useTasks().data ?? [];
  const routines = useRoutines().data ?? [];
  const notes = useNotes().data ?? [];
  const open = tasks.filter(t => t.status !== 'done');
  const mine = open.filter(t => t.assigneeId === ws.me.id || t.subtasks.some(s => s.assigneeId === ws.me.id && !s.done)).length;
  const section = path.split('/')[1] || 'quadro';
  const go = (to: string, extra?: () => void) => { extra?.(); set({ sidebarOpen: false }); nav(to); };
  const goBoard = (project: string) => go('/', () => { setScope({ project, ...(project === 'mine' ? { person: 'all' } : {}) }); set({ highlight: null }); });
  const onBoard = (p: string) => section === 'quadro' && scope.project === p;

  return (
    <aside className="sidebar" aria-label="Navegação">
      <div className="brand"><Icon name="logo" /><span>Quadro</span></div>
      <button className="sb-search" onClick={() => { set({ sidebarOpen: false }); openPalette(); }} aria-label="Buscar em tudo (Ctrl+K)">
        <Icon name="search" /><span className="lbl">Buscar</span><kbd>{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      <nav className="sb-list" aria-label="Seções">
        <Item icon="board" label="Quadro" count={open.length} current={onBoard('all')} onClick={() => goBoard('all')} />
        <Item icon="me" label="Minhas tarefas" count={mine} current={onBoard('mine')} onClick={() => goBoard('mine')} />
        <Item icon="recL" label="Rotinas" count={routines.filter(r => r.active).length} current={section === 'rotinas'} onClick={() => go('/rotinas')} />
        <Item icon="mapa" label="Mapa societário" current={section === 'mapa'} onClick={() => go('/mapa')} />
        <Item icon="flow" label="Processos" current={section === 'processos'} onClick={() => go('/processos')} />
        <Item icon="cal" label="Agenda" current={section === 'agenda'} onClick={() => go('/agenda')} />
        <Item icon="note" label="Notas" current={section === 'notas'} onClick={() => go('/notas', () => set({ notesFilter: 'all' }))} />
      </nav>

      {section === 'agenda' && (
        <div><h2 className="sb-h">Calendários</h2><div className="sb-list">
          {[...CALENDARS, ...(outlookOn ? [OUTLOOK_CALENDAR] : [])].map(c => (
            <label className="sb-cal" key={c.id}>
              <input type="checkbox" checked={!hiddenCalendars.includes(c.id)}
                onChange={e => set({ hiddenCalendars: e.target.checked ? hiddenCalendars.filter(x => x !== c.id) : [...hiddenCalendars, c.id] })} />
              <span className="box" style={{ '--c': c.color } as React.CSSProperties}><Icon name="tick" /></span>{c.name}
            </label>
          ))}
        </div></div>
      )}
      {section === 'notas' && (
        <div><h2 className="sb-h">Minhas notas</h2><div className="sb-list">
          <Item icon="note" label="Todas" count={notes.length} current={notesFilter === 'all'} onClick={() => set({ notesFilter: 'all' })} />
          <Item icon="pinS" label="Fixadas" count={notes.filter(n => n.pinned).length} current={notesFilter === 'pinned'} onClick={() => set({ notesFilter: 'pinned' })} />
        </div></div>
      )}

      <div>
        <div className="sb-h-row"><h2 className="sb-h">Projetos</h2>
          {ws.canWrite && <button className="sb-mini" onClick={() => openSheet({ kind: 'settings', tab: 'projects' })} title="Gerenciar projetos" aria-label="Gerenciar projetos"><Icon name={ws.settings.projects.length ? 'edit' : 'plus'} /></button>}
        </div>
        <div className="sb-list">
          {ws.settings.projects.length
            ? ws.settings.projects.map(p => <Item key={p.id} dot={p.color} label={p.name} count={open.filter(t => t.projectId === p.id).length} current={onBoard(p.id)} onClick={() => goBoard(p.id)} />)
            : <><span className="sb-empty">Nenhum projeto ainda.</span>{ws.canWrite && <button className="side-link" onClick={() => openSheet({ kind: 'settings', tab: 'projects' })}>Criar projetos da área</button>}</>}
        </div>
      </div>

      <div className="me-box">
        {ws.canWrite && <Item icon="gear" label="Ajustes da área" onClick={() => openSheet({ kind: 'settings' })} />}
        <Item icon="bell" label="Avisos" count={notices} onClick={() => openSheet({ kind: 'notifications' })} />
        <Item icon="people" label="Pessoas" count={ws.isAdmin ? ws.people.filter(p => !p.active).length || ws.members.length : ws.members.length} onClick={() => openSheet({ kind: 'people' })} />
        <div className="me"><Avatar id={ws.me.id} /><span className="lbl">{ws.me.name || 'Você'}</span></div>
        <div className={`sync ${ws.canWrite ? '' : 'ro'}`}><i />{ws.canWrite ? 'Compartilhado com a equipe' : 'Somente leitura'}</div>
        <div className="sb-acts">
          {/* Na demonstração no navegador não há servidor para o Claude se conectar. */}
          {!import.meta.env.VITE_DEMO && <button className="sb-link" onClick={() => openSheet({ kind: 'claude' })}>Conectar ao Claude</button>}
          <button className="sb-signout" onClick={signOut}>Sair</button>
        </div>
      </div>
    </aside>
  );
}
